import { useEffect, useRef, useState } from 'react'
import { GripVertical, Loader2, X, ImageIcon } from 'lucide-react'
import {
  useLineItemImages,
  useReorderLineItemImage,
  useUnassignImageFromLine,
} from '@/hooks/useImages'
import { getImage } from '@/api/images'
import { useThumbnailTrackingStore } from '@/stores/useThumbnailTrackingStore'
import type { LineItemImageRead } from '@/types/image'

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

function SortableImageCard({
  image,
  onRemove,
}: {
  image: LineItemImageRead
  onRemove: (imageId: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative rounded-lg border bg-white overflow-hidden group ${
        isDragging ? 'opacity-50 shadow-lg z-10' : ''
      } ${image.is_default ? 'ring-2 ring-primary' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        title="Drag to reorder"
        aria-label="Drag image to reorder"
        className="absolute top-1.5 left-1.5 z-10 cursor-grab active:cursor-grabbing rounded bg-white/90 p-1 shadow-sm"
      >
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <button
        onClick={() => onRemove(image.image_id)}
        aria-label={`Remove ${image.name || image.original_filename || 'image'}`}
        className="absolute top-1.5 right-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors cursor-pointer"
      >
        <X className="h-3 w-3" />
      </button>
      {image.is_default && (
        <span className="absolute bottom-7 right-1.5 z-10 bg-primary text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
          Default
        </span>
      )}
      <div className="aspect-square flex items-center justify-center bg-gray-50">
        <img
          src={image.thumbnail_url || image.download_url || ''}
          alt={image.name || image.original_filename || 'Image'}
          className="w-full h-full object-cover"
          draggable={false}
        />
      </div>
      <div className="px-2 py-1 truncate">
        <p className="text-xs text-muted-foreground truncate">
          {image.name || image.original_filename}
        </p>
      </div>
    </div>
  )
}

interface ReorderableImageGalleryProps {
  planId: string
  versionId: string
  lineId: string
}

export function ReorderableImageGallery({
  planId,
  versionId,
  lineId,
}: ReorderableImageGalleryProps) {
  const { data: images, isLoading } = useLineItemImages(
    planId,
    versionId,
    lineId
  )
  const reorderMutation = useReorderLineItemImage(planId, versionId, lineId)
  const unassignMutation = useUnassignImageFromLine(planId, versionId, lineId)

  const [orderedImages, setOrderedImages] = useState<LineItemImageRead[]>([])
  const pendingRemovals = useRef(new Set<string>())
  const addThumbnailEntry = useThumbnailTrackingStore(s => s.addEntry)

  const trackThumbnailForImage = async (imageId: string) => {
    try {
      const imageDetail = await getImage(imageId)
      if (
        imageDetail.thumbnail_status === 'PENDING' &&
        imageDetail.events_url
      ) {
        addThumbnailEntry({
          lineId,
          planId,
          versionId,
          eventsUrl: imageDetail.events_url,
        })
      }
    } catch {
      // Ignore — fallback to query invalidation
    }
  }

  const handleRemove = (imageId: string) => {
    const wasDefault = orderedImages[0]?.image_id === imageId
    const nextDefaultImageId =
      wasDefault && orderedImages.length > 1 ? orderedImages[1].image_id : null

    pendingRemovals.current.add(imageId)
    setOrderedImages(prev => prev.filter(img => img.image_id !== imageId))
    unassignMutation.mutate(imageId, {
      onSuccess: () => {
        if (nextDefaultImageId) {
          trackThumbnailForImage(nextDefaultImageId)
        }
      },
      onSettled: () => {
        pendingRemovals.current.delete(imageId)
      },
    })
  }

  useEffect(() => {
    if (images) {
      setOrderedImages(
        images.filter(img => !pendingRemovals.current.has(img.image_id))
      )
    }
  }, [images])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = orderedImages.findIndex(img => img.id === active.id)
    const newIndex = orderedImages.findIndex(img => img.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const newOrder = [...orderedImages]
    const [moved] = newOrder.splice(oldIndex, 1)
    newOrder.splice(newIndex, 0, moved)
    setOrderedImages(newOrder)

    const defaultChanged = newOrder[0]?.image_id !== orderedImages[0]?.image_id

    const afterId = newIndex > 0 ? newOrder[newIndex - 1].id : null
    const beforeId =
      newIndex < newOrder.length - 1 ? newOrder[newIndex + 1].id : null

    reorderMutation.mutate(
      {
        imageId: moved.image_id,
        data: { after_id: afterId, before_id: beforeId },
      },
      {
        onSuccess: () => {
          if (defaultChanged) {
            trackThumbnailForImage(newOrder[0].image_id)
          }
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!orderedImages.length) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted mb-2">
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">No images assigned yet</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Upload or select images to get started
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2">
        <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-sm font-medium text-foreground">
          Drag images to reorder. The first image is used as default.
        </p>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedImages.map(img => img.id)}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-2 gap-2">
            {orderedImages.map(image => (
              <SortableImageCard
                key={image.id}
                image={image}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}
